/**
 * ImportStatusBanner — shows retag job progress when a mailbox is added
 * to a unified inbox and existing mail is being backfilled (issue #369).
 *
 * Follows the SubscriptionExpiredBanner pattern: receives `stats` as a prop
 * (already fetched by the parent via useInboxStats), returns null when not
 * applicable, and renders a conditional banner with icon + text.
 *
 * The banner is driven by `stats.import_status`, which the stats endpoint
 * populates from the most recent `inbox_retag_jobs` row. The 60s polling
 * interval on useInboxStats refreshes the progress automatically.
 */
import React from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

export default function ImportStatusBanner({ stats }) {
    const importStatus = stats?.import_status;
    if (!importStatus || importStatus.status === 'idle') return null;

    if (importStatus.status === 'importing') {
        return (
            <div
                className="flex items-center gap-2 px-4 py-2.5 border-b bg-blue-50 border-blue-200 text-blue-800 text-sm"
                role="status"
                aria-live="polite"
                data-testid="import-status-banner"
            >
                <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" aria-hidden="true" />
                <span className="min-w-0">
                    Importing existing mail for the newly added mailbox…
                    {importStatus.total > 0 &&
                        ` (${importStatus.tagged} / ${importStatus.total} tagged)`}
                </span>
            </div>
        );
    }

    if (importStatus.status === 'complete' && importStatus.tagged > 0) {
        return (
            <div
                className="flex items-center gap-2 px-4 py-2.5 border-b bg-green-50 border-green-200 text-green-800 text-sm"
                role="status"
                aria-live="polite"
                data-testid="import-status-banner"
            >
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                    Import complete — {importStatus.tagged} message
                    {importStatus.tagged === 1 ? '' : 's'} tagged into this inbox.
                </span>
            </div>
        );
    }

    if (importStatus.status === 'error') {
        return (
            <div
                className="flex items-center gap-2 px-4 py-2.5 border-b bg-red-50 border-red-200 text-red-800 text-sm"
                role="status"
                aria-live="assertive"
                data-testid="import-status-banner"
            >
                <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                    Import failed — {importStatus.tagged} message
                    {importStatus.tagged === 1 ? '' : 's'} tagged before the error.
                    Please try again or contact support.
                </span>
            </div>
        );
    }

    return null;
}
